import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Form, Spinner, InputGroup } from "react-bootstrap";
import { FaEye, FaExchangeAlt, FaUndo, FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import InfoPill from "../Common/InfoPill";
import { formatDateTimeToDDMMYYYY12Hr } from "../../utils/dateUtils";
import DateRangePicker from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";

const ReturnsList = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const isVendor = user?.user_type === "vendor";
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateRange, setDateRange] = useState(null);

  // Helper to format date to YYYY-MM-DD for backend query
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch returns with backend filtering
  const fetchReturns = async (searchParam = searchInput) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchParam && searchParam.trim()) {
        queryParams.append("search", searchParam.trim());
      }
      if (statusFilter) {
        queryParams.append("status", statusFilter);
      }
      if (typeFilter) {
        queryParams.append("type", typeFilter);
      }
      if (dateRange && dateRange[0]) {
        queryParams.append("start_date", formatDateToYYYYMMDD(dateRange[0]));
      }
      if (dateRange && dateRange[1]) {
        queryParams.append("end_date", formatDateToYYYYMMDD(dateRange[1]));
      }

      const response = await api.get(`/api/returns?${queryParams.toString()}`);
      if (response.data && response.data.success) {
        const rawData = response.data.data || [];
        const formatted = rawData.map((item) => ({
          ...item,
          customer_full_name: `${item.customer_first_name || ""} ${item.customer_last_name || ""}`.trim() || "Unknown",
          display_order_no: item.order_no || (item.order_id ? `ORD-${item.order_id.slice(-8).toUpperCase()}` : "N/A"),
          formatted_refund: item.type === "return" 
            ? `₹${parseFloat((isVendor && item.vendor_refund_amount !== undefined) ? item.vendor_refund_amount : (item.refund_amount || 0)).toFixed(2)}` 
            : "-",
        }));
        setReturns(formatted);
      } else {
        toast.error("Failed to fetch return requests");
      }
    } catch (err) {
      console.error("Error fetching returns:", err);
      toast.error("Failed to load return requests");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search when searchInput changes
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchReturns(searchInput);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, statusFilter, typeFilter, dateRange]);

  const handleResetFilters = () => {
    setSearchInput("");
    setStatusFilter("");
    setTypeFilter("");
    setDateRange(null);
  };

  const getStatusBadge = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusMap = {
      pending: { variant: "warning", text: "Pending Review" },
      approved: { variant: "info", text: "Approved" },
      pickup_scheduled: { variant: "primary", text: "Pickup Scheduled" },
      pickup_completed: { variant: "info", text: "In Transit" },
      received: { variant: "secondary", text: "Package Received" },
      replacement_dispatched: { variant: "primary", text: "Replacement Dispatched" },
      completed: { variant: "success", text: "Completed" },
      rejected: { variant: "danger", text: "Rejected" },
      cancelled: { variant: "dark", text: "Cancelled" },
    };

    const config = statusMap[key] || {
      variant: "secondary",
      text: status ? status.replace("_", " ").toUpperCase() : "UNKNOWN",
    };

    return (
      <Badge bg={config.variant} className="px-2 py-1">
        {config.text}
      </Badge>
    );
  };

  const tableHeaders = [
    {
      prop: "display_order_no",
      title: "Order No.",
      isSortable: true,
      cell: (row) => (
        <div>
          <Link
            to={`/admin/orders/details/${row.order_id}`}
            className="text-decoration-none fw-bold text-primary"
            style={{ cursor: "pointer" }}
          >
            {row.display_order_no}
          </Link>
        </div>
      ),
    },
    {
      prop: "customer_full_name",
      title: "Customer",
      isSortable: true,
      cell: (row) => (
        <div>
          <div className="fw-semibold">{row.customer_full_name}</div>
          <small className="text-muted">{row.customer_phone || "-"}</small>
        </div>
      ),
    },
    {
      prop: "type",
      title: "Type",
      isSortable: true,
      cell: (row) =>
        row.type === "exchange" ? (
          <span className="text-primary fw-bold d-inline-flex align-items-center">
            <FaExchangeAlt className="me-1" /> Exchange
          </span>
        ) : (
          <span className="text-success fw-bold d-inline-flex align-items-center">
            <FaUndo className="me-1" /> Return
          </span>
        ),
    },
    {
      prop: "formatted_refund",
      title: "Refund Amount",
      isSortable: true,
      cell: (row) => (
        <div>
          <span className={row.type === "return" ? "text-success fw-bold" : "text-muted"}>
            {row.formatted_refund}
          </span>
          {row.upi_id && (
            <div
              className="small font-monospace text-primary text-truncate"
              style={{ maxWidth: "150px", fontSize: "11px" }}
              title={`UPI: ${row.upi_id}`}
            >
              UPI: {row.upi_id}
            </div>
          )}
        </div>
      ),
    },
    {
      prop: "created_at",
      title: "Request Date",
      isSortable: true,
      cell: (row) => (
        <span className="small">{formatDateTimeToDDMMYYYY12Hr(row.created_at)}</span>
      ),
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => navigate(`/admin/returns/details/${row.id}`)}
            title="View Details"
            className="d-inline-flex align-items-center justify-content-center px-3 py-1"
            style={{
              whiteSpace: "nowrap",
              minWidth: "75px",
              width: "auto",
              height: "32px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "500",
              gap: "4px",
            }}
          >
            <FaEye size={13} />
            <span>View</span>
          </Button>
        </div>
      ),
    },
  ];

  const labels = {
    beforeSelect: " ",
  };

  const hasActiveFilters = Boolean(searchInput || statusFilter || typeFilter || dateRange);

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Link className="breadcrum" to="/">
            Home{"/"}
            <i
              className="fa-solid fa-chevron-right"
              style={{ marginLeft: "1px", marginRight: "1px" }}
            ></i>{" "}
            Returns & Exchanges
          </Link>
          {isVendor && (
            <Badge bg="info" className="px-2 py-1 text-dark" style={{ fontSize: "11px", fontWeight: "600" }}>
              Vendor Portal View
            </Badge>
          )}
        </div>
      </div>

      <DatatableWrapper
        body={Array.isArray(returns) ? returns : []}
        headers={tableHeaders}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 10, options: [5, 10, 15, 20, 50] },
        }}
      >
        {/* Backend Filter Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
            {/* Backend Search Input */}
            <div style={{ minWidth: "220px", maxWidth: "280px" }}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by Order #, Customer..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="border-start-0"
                />
                {searchInput && (
                  <Button
                    variant="outline-secondary"
                    className="border-start-0 bg-white"
                    onClick={() => setSearchInput("")}
                  >
                    <FaTimes size={11} />
                  </Button>
                )}
              </InputGroup>
            </div>

            {/* Backend Status Filter */}
            <div style={{ width: "160px" }}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending Review</option>
                <option value="pickup_scheduled">Pickup Scheduled</option>
                <option value="received">Package Received</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </div>

            {/* Backend Type Filter */}
            <div style={{ width: "160px" }}>
              <Form.Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                size="sm"
              >
                <option value="">All Types</option>
                <option value="return">Return (Refund)</option>
                <option value="exchange">Exchange (Replacement)</option>
              </Form.Select>
            </div>

            {/* Backend Date Range Picker */}
            <div style={{ width: "240px" }}>
              <DateRangePicker
                placeholder="Filter by Date Range"
                value={dateRange}
                onChange={setDateRange}
                cleanable
                size="sm"
                block
              />
            </div>

            {hasActiveFilters && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleResetFilters}
                title="Reset filters"
              >
                <FaFilter className="me-1" /> Reset
              </Button>
            )}

            <PaginationOptions labels={labels} />
          </div>

          <div>
            <InfoPill left="Total Requests" right={returns.length} />
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle mb-0">
            <TableHeader />
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                    <span className="text-muted">Loading return requests...</span>
                  </td>
                </tr>
              </tbody>
            ) : returns.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    {isVendor
                      ? "No return or exchange requests found for your store products."
                      : "No return or exchange requests found matching active backend filters."}
                  </td>
                </tr>
              </tbody>
            ) : (
              <TableBody />
            )}
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-start mt-3">
          <Pagination
            classes={{
              button: "bg-dark",
            }}
          />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default ReturnsList;
